import useSWR from 'swr';
import { useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { Banner } from '@yoweb/ui/components/Banner';
import { getColor, getSpace } from '@yoweb/ui/styles/utils/theme';
import { WarningIcon } from '@yoweb/ui/components/Icon/WarningIcon';
import { Button } from '@yoweb/ui/components/buttons/Button';
import { useTranslation } from '@yoweb/next-i18next';

const DEFAULT_EXPECTED_RESPONSE = process.env.NEXT_PUBLIC_APP_VERSION ?? '';

type UpdateBannerProps = {
  expectedResponse?: string;
  timeout?: number;
  url?: string;
  id?: string;
};

export const UpdateBanner = ({
  expectedResponse = DEFAULT_EXPECTED_RESPONSE,
  timeout = 90000, // 90s in ms
  url = '/api/healthz',
  ...rest
}: UpdateBannerProps) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [versionHasChanged, setVersionHasChanged] = useState<boolean>(false);

  useSWR(url, fetch, {
    refreshInterval: versionHasChanged ? 0 : timeout,
    revalidateOnFocus: false,
    shouldRetryOnError: true,
    async onSuccess(response) {
      const text = await response.text();
      setVersionHasChanged(text !== expectedResponse);
    },
  });

  if (!versionHasChanged) {
    return null;
  }

  return (
    <StyledBanner
      actions={[
        <Button
          onClick={() => router.reload()}
          size="sm"
          key="update-banner-action-0"
          variant="secondary"
        >
          {t('update-banner.refresh')}
        </Button>,
      ]}
      backgroundColor="warningLight"
      icon={<WarningIcon size={24} variant="warning" />}
      dismissible
      title={t('update-banner.title')}
      subtitle={t('update-banner.subtitle')}
      data-testid="update-banner"
      {...rest}
    />
  );
};

const StyledBanner = styled(Banner)`
  && {
    border-radius: 0;
    padding: ${getSpace('normal2')} ${getSpace('normal1')};
    background-color: ${getColor('warningLight')};
  }
`;
